/**
 * SubjectLottieIcon Component
 *
 * Displays animated Lottie icon for a subject.
 * Animation plays on hover.
 */

'use client'

import React, { useRef, useEffect, useState } from 'react'
import lottie, { AnimationItem } from 'lottie-web'
import { CourseSubject } from '@/types/firebase'
import { getSubjectLottieIcon } from '@/config/subject-lottie-icons'

interface SubjectLottieIconProps {
  /**
   * Course subject to display icon for
   */
  subject: CourseSubject

  /**
   * Size in pixels (width and height)
   */
  size?: number

  /**
   * Whether the animation should be playing (controlled by parent).
   * Pass the isHovered state from parent component.
   */
  isPlaying?: boolean

  /**
   * Whether to loop animation
   */
  loop?: boolean

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * Opacity (0-1)
   */
  opacity?: number
}

export const SubjectLottieIcon: React.FC<SubjectLottieIconProps> = ({
  subject,
  size = 80,
  isPlaying = false,
  loop = false,
  className = '',
  opacity = 0.8,
}): React.JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<AnimationItem | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  const config = getSubjectLottieIcon(subject)

  // Initialize Lottie animation
  useEffect(() => {
    if (!containerRef.current) return
    const loadAnimation = async () => {
      try {
        // Fetch from public/images/lottie/
        const response = await fetch(`/images/lottie/${config.fileName}`)
        if (!response.ok) {
          throw new Error(`Failed to load animation: ${response.statusText}`)
        }
        const animationData = await response.json()

        // Initialize lottie animation
        const anim = lottie.loadAnimation({
          container: containerRef.current!,
          renderer: 'svg',
          loop: loop,
          autoplay: false, // Don't autoplay, we'll control it manually
          animationData: animationData,
        })

        animationRef.current = anim

        // Listen for animation events for debugging
        anim.addEventListener('DOMLoaded', () => {
          setIsLoaded(true)
        })

        anim.addEventListener('complete', () => {
          if (loop) {
            anim.goToAndPlay(0, true)
          }
        })

        // Set animation to first frame initially
        anim.goToAndStop(0, true)
      } catch (error) {
        console.error(
          `[Lottie] Failed to load animation for ${subject}:`,
          error,
        )
      }
    }

    loadAnimation()

    // Cleanup
    return () => {
      if (animationRef.current) {
        animationRef.current.destroy()
        animationRef.current = null
      }
      setIsLoaded(false)
    }
  }, [config.fileName, subject, loop])

  // Handle animation playback based on isPlaying prop
  useEffect(() => {
    if (!animationRef.current || !isLoaded) {
      return
    }

    if (isPlaying) {
      // Stop first to ensure clean state
      animationRef.current.stop()
      // Play the animation from the beginning
      animationRef.current.play()
    } else {
      // Stop and reset to first frame when not playing
      animationRef.current.stop()
      animationRef.current.goToAndStop(0, true)
    }
  }, [isPlaying, isLoaded, subject])

  return (
    <div
      ref={containerRef}
      style={{ width: size, height: size, opacity }}
      className={`transition-opacity duration-300 ${className}`}
    />
  )
}

export default SubjectLottieIcon
